import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import PermissionsContainer from '../PermissionsContainer';
import { useToast } from '../../contexts/ToastContext';
import printingService, { printErrorMessage } from '../../services/printing.service';
import { PRINT_SEND_COMPONENT_ID, PRINT_SEND_COMPONENT_NAME } from './PrintButton';

export interface PrintTestPageButtonProps {
  /** cms.printers.installname of the queue to test. */
  printer: string;
  /** cms.printers.type — only LASER queues take the PDF test sheet. */
  type?: string | null;
  className?: string;
}

/**
 * "Print Test Page" for Table Maintenance > Printers: sends one sheet to the row's
 * queue asking whoever finds it to contact the admin on Teams. Same `print-send`
 * gate as every other print control. No dialog: the printer is the row itself.
 */
const PrintTestPageButton: React.FC<PrintTestPageButtonProps> = ({ printer, type, className = '' }) => {
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  const isLaser = (type ?? 'LASER').toUpperCase() === 'LASER';
  const disabled = busy || !printer || !isLaser;
  const title = !isLaser
    ? 'Test pages are for LASER printers only'
    : `Send a test page to ${printer}`;

  const send = async () => {
    setBusy(true);
    try {
      const r = await printingService.printTestPage(printer);
      showToast({ type: 'success', message: `Test page sent to ${r.display_name} (job ${r.job_id})` });
    } catch (err) {
      showToast({ type: 'error', message: printErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionsContainer
      componentId={PRINT_SEND_COMPONENT_ID}
      componentName={PRINT_SEND_COMPONENT_NAME}
      defaultHidden
      showHandle={false}
    >
      <button
        type="button"
        onClick={send}
        disabled={disabled}
        title={title}
        className={className || 'inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed'}
      >
        <PrinterIcon className="w-4 h-4" aria-hidden="true" />
        {busy ? 'Sending…' : 'Print Test Page'}
      </button>
    </PermissionsContainer>
  );
};

export default PrintTestPageButton;
